/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable camelcase */

import { Menu, MenuButton, MenuDivider, MenuItem } from '@szhsin/react-menu';
import { useTranslation } from 'react-i18next';

import Render from 'components/render';
import { RenderByExchange } from 'components/render/RenderByExchangeType';
import { IS_DEMO, IS_INDIAN_EXCHANGE } from 'constants/constants';
import { generateAssetUnitLabel } from 'helpers/localized';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import {
  capitalize,
  decimalMultiplication,
  isOptionCombos,
  isOptions,
  js_yyyy_mm_dd_hh_mm_ss,
  numberCommaSeparator,
} from 'helpers/utils';
import i18n from 'i18n/config';
import { ArrowDownThin, LearnMoreIcon } from 'styles/assets/icons';
import { ContractType } from 'types/IProducts';

import defaultStyles from './index.module.scss';

const contractSpecsUrl = (contractType, symbol, underlying) => {
  if (contractType === ContractType.OptionsCombos) {
    return `/contracts#${symbol}`;
  }
  if (isOptions(contractType)) {
    return `/contracts#${underlying}`;
  }
  return `/contracts#${symbol}`;
};

// eslint-disable-next-line react/prop-types
const ProductDetails = ({ selectedProduct, classNames }) => {
  const { t } = useTranslation(['trading']);
  const {
    symbol,
    short_description,
    description,
    notional_type,
    settlement_time,
    contract_type,
    spot_index,
    initial_margin,
    underlying_asset,
    settling_asset,
    trading_status,
    // impact_size,
    maintenance_margin,
    contract_value,
    contract_unit_currency,
    // is_quanto,
    position_size_limit,
  } = selectedProduct || {};

  const spotIndexSymbol = spot_index.symbol;
  const isMove = contract_type === 'move_options';
  const isCall = contract_type === 'call_options';
  const isPut = contract_type === 'put_options';
  const isOptionsCombos = contract_type === 'options_combos';

  const contractValue = `${contract_value} ${contract_unit_currency}`;

  const positionLimit = decimalMultiplication(position_size_limit, contract_value);
  const settlementCurrency = IS_INDIAN_EXCHANGE
    ? i18n.t('auth:inr')
    : settling_asset.symbol;
  const isOptionsContract = isOptions(contract_type);
  const isTracker = contract_type === ContractType.Tracker;

  // const spotPrice = spotPriceSelectorBySymbol(spotIndexSymbol);
  // const currentValue = (Number(contract_value) * Number(spotPrice)).toFixed(
  // underlying_asset.minimum_precision
  // );

  const onContractDetailsClick = () => {
    trackMixpanelEvent('Contract Page View Clicked - Trade', {
      product_name: symbol,
      product_type: contract_type,
      source: 'Trade Page',
    });
  };

  const onFullSpecClick = () => {
    trackMixpanelEvent('See Full Contract Specifications Clicked - Trade', {
      product_name: symbol,
      product_type: contract_type,
    });
  };

  // const quantoContractValue =
  //   contract_type !== 'spot'
  //     ? `${Number(contract_value)} ${settling_asset.symbol} per ${
  //         quoting_asset.symbol
  //       } (Current value = ${currentValue} ${underlying_asset.symbol})`
  //     : 1;

  const styles = { ...defaultStyles, ...classNames };

  return (
    (<Menu
      menuButton={({ open }) => (
        <MenuButton
          data-testid="product-details-menu-button"
          onClick={onContractDetailsClick}
          className={`${open ? styles.active : ''} ${styles.button}`}
        >
          {/* {t('trading:contractDetails')}{' '} */}
          {isCall || isPut ? (
            <>
              <div className={styles.label} data-palette="ProductDetails">
                {generateAssetUnitLabel('trading:unitSize', { capitalize: true })}
              </div>
              <div className={styles.value} data-palette="ProductDetails">{contractValue}</div>
            </>
          ) : (
            <div data-palette="ProductDetails"> {t('trading:contractDetails')} </div>
          )}
          <ArrowDownThin className={open ? styles.rotate : styles.arrow} />{' '}
        </MenuButton>
      )}
      menuClassName={styles['product-menu-container']}
    >
      <MenuItem className={styles.description}>
        {short_description || description}
      </MenuItem>
      <MenuDivider className={styles.divider} />
      {isOptions(contract_type) ? (
        <MenuItem className={styles['product-details-item']}>
          <div className={styles.label}>{t('trading:exerciseStyle')}</div>
          <div className={styles.value}>{t('trading:optionExerciseStyle')}</div>
        </MenuItem>
      ) : (
        <Render when={!isTracker}>
          <MenuItem className={styles['product-details-item']}>
            <div className={styles.label}>{t('trading:type')}</div>
            <div className={styles.value}>
              {notional_type === 'vanilla' ? t('trading:linear') : t('trading:inverse')}
            </div>
          </MenuItem>
        </Render>
      )}
      {!isOptions(contract_type) && (
        <MenuItem className={styles['product-details-item']}>
          <div className={styles.label}>
            {generateAssetUnitLabel('trading:unitSize', { capitalize: true })}
          </div>
          <div className={styles.value}>{contractValue}</div>
        </MenuItem>
      )}
      <Render when={isTracker}>
        <MenuItem className={styles['product-details-item']}>
          <div className={styles.label}>Min Lot Size</div>
          <div className={styles.value}>
            {selectedProduct?.product_specs?.min_order_size}
          </div>
        </MenuItem>
      </Render>
      {(contract_type === 'futures' || isMove || isCall || isPut || isOptionsCombos) && (
        <MenuItem className={styles['product-details-item']}>
          <div className={styles.label}>{t('trading:expiresOn')}</div>
          <div className={styles.value}>
            {js_yyyy_mm_dd_hh_mm_ss(new Date(settlement_time))}
          </div>
        </MenuItem>
      )}
      {/* <MenuItem className={styles['product-details-item']}>
        <div className={styles.label}>
          {t('trading:contractSize')}/ {t('trading:lotSize')}
        </div>
        <div className={styles.value}>{contractValue}</div>
      </MenuItem> */}
      {/* <MenuItem className={styles['product-details-item']}>
        <div className={styles.label}>{t('trading:lotSize')}</div>
        <div className={styles.value}>
          {' '}
          1 {t('trading:makerTaker.contract')}
        </div>
      </MenuItem>

      <MenuItem className={styles['product-details-item']}>
        <div className={styles.label}>{t('trading:contractVal')}</div>
        <div className={styles.value}>
          {`${is_quanto ? quantoContractValue : contractValue}`}
        </div>
      </MenuItem> */}
      <MenuItem className={styles['product-details-item']}>
        <div className={styles.label}>
          <RenderByExchange
            global={<>{i18n.t('trading:settlementCurrency')}</>}
            indian={<>{i18n.t('trading:effectiveSettlementCurrency')}</>}
          />
        </div>
        <div className={styles.value}>{settlementCurrency}</div>
      </MenuItem>
      {/* {isMove || isCall || isPut ? (
        <MenuItem className={styles['product-details-item']}>
          <div className={styles.label}>{t('trading:strikePrice')}</div>
          <div className={styles.value}>
            {Number(strike_price).toFixed(quoting_asset.minimum_precision)}
          </div>
        </MenuItem>
      ) : null} */}
      <Render when={!isOptionsContract && !isTracker}>
        <MenuItem className={styles['product-details-item']}>
          <div className={styles.label}>{t('trading:initialMargin')}</div>
          <div className={styles.value}>{parseFloat(initial_margin)} %</div>
        </MenuItem>
      </Render>
      <Render when={isOptionsContract}>
        <MenuItem className={styles['product-details-item']}>
          <div className={styles.label}>{t('trading:initialMarginShort')}</div>
          {isOptionCombos(contract_type) ? (
            <div
              className={`${styles.value} ${styles.maxWidth}`}
              data-palette="ProductDetails">
              min({t('trading:indexPrice')}*0.5%, abs(Strike2 - Strike1)){' '}
              {t('trading:extraText')}
            </div>
          ) : (
            <div className={styles.value} data-palette="ProductDetails">
              {parseFloat(initial_margin)} % {t('trading:initialMarginExtra')}
              <br />
              {t('trading:extraText')}
            </div>
          )}
        </MenuItem>
      </Render>
      <Render when={!isTracker}>
        <MenuItem className={styles['product-details-item']}>
          <div className={styles.label}>{t('trading:maxLeverage')}</div>
          <div className={styles.value}>{parseInt(100 / initial_margin, 10)}x</div>
        </MenuItem>
      </Render>
      <Render when={!isOptionsContract && !isTracker}>
        <MenuItem className={styles['product-details-item']}>
          <div className={styles.label}>{t('trading:maintenanceMargin')}</div>
          <div className={styles.value}>{parseFloat(maintenance_margin)} %</div>
        </MenuItem>
      </Render>
      <Render when={isOptionsContract}>
        <MenuItem className={styles['product-details-item']}>
          <div className={styles.label}>{t('trading:maintenanceMarginShort')}</div>
          {isOptionCombos(contract_type) ? (
            <div
              className={`${styles.value} ${styles.maxWidth}`}
              data-palette="ProductDetails">
              min({t('trading:indexPrice')}*0.25%, abs(Strike2 - Strike1)*0.5)
              {t('trading:extraText')}
            </div>
          ) : (
            <div className={styles.value} data-palette="ProductDetails">
              {parseFloat(maintenance_margin)} % {t('trading:initialMarginExtra')}
              <br />
              {t('trading:extraText')}
            </div>
          )}
        </MenuItem>
      </Render>
      <MenuItem className={styles['product-details-item']}>
        <div className={styles.label}>{t('trading:underlyingIndex')}</div>
        <div className={styles.value}>
          <a
            href={`/indices/#${spotIndexSymbol}`}
            rel="noopener noreferrer"
            target="_blank"
            data-testid="product-detail-underlying-index"
            className={styles.underlying_index}
          >
            {spotIndexSymbol}
          </a>
        </div>
      </MenuItem>
      <MenuItem className={styles['product-details-item']}>
        <div className={styles.label}>{t('trading:positionLimit')}</div>
        <div className={styles.value}>
          <span className={styles.position_limit}>
            {`${positionLimit} ${contract_unit_currency}`}
          </span>
          <br />
          <span>
            {numberCommaSeparator(position_size_limit)} {t('trading:contracts')}
          </span>
          {/* {`${positionLimit} ${contract_unit_currency} (${numberCommaSeparator(
            position_size_limit
          )} ${t('trading:contracts')})`} */}
        </div>
      </MenuItem>
      {/* <MenuItem className={styles['product-details-item']}>
        <div className={styles.label}>{t('trading:impactSize')}</div>
        <div className={styles.value}>
          {parseInt(impact_size, 10)} {t('trading:contracts')}
        </div>
      </MenuItem> */}
      <MenuItem className={styles['product-details-item']}>
        <div className={styles.label}>{t('trading:status')}</div>
        <div className={styles.value}>{capitalize(trading_status)}</div>
      </MenuItem>
      <MenuItem className={styles['product-details-item']}>
        <div className={styles.label}>{t('trading:holdingCost')}</div>
        <div className={styles.value}>
          {selectedProduct?.product_specs?.holding_cost_percentage}%
        </div>
      </MenuItem>
      {!IS_DEMO && <MenuDivider className={styles.divider} />}
      {!IS_DEMO && (
        <MenuItem
          data-testid="contract-spec-button"
          className={styles['contract-spec-button']}
          onClick={onFullSpecClick}
          href={contractSpecsUrl(contract_type, symbol, underlying_asset.symbol)}
          target="_blank"
        >
          <span>{t('trading:contractSpecs')}</span> <LearnMoreIcon />
        </MenuItem>
      )}
    </Menu>)
  );
};

export default ProductDetails;
